"use client";

import React, { useEffect, useState, useRef } from "react";
import { Plus, Shield, Info, Check, X, BookOpen, Clock, Users, ShieldAlert, ChevronDown } from "lucide-react";
import { fetchWithAuth, API_BASE } from "@/lib/auth";
import { useWorkspace } from "@/lib/context/WorkspaceContext";

interface GigTypeGroup {
    entity_type: string;
    entity_id: number;
    entity_name?: string;
}

interface GigType {
    id: string;
    name: string;
    slug: string;
    description: string | null;
    cadence: string;
    time_commitment: string | null;
    default_discovery_mode: string;
    recognition_tier: string;
    is_confidential_intake: boolean;
    skip_staff_review: boolean;
    open_to_all?: boolean;
    program_journey_scope?: string | null;
    membership_journey_scope?: string | null;
    owning_groups?: GigTypeGroup[];
    status?: string;
}

interface GroupOption {
    entity_type: string;
    entity_id: number;
    name: string;
}

interface GroupItem {
    id: number;
    name: string;
}

const PROGRAM_JOURNEYS = [
    "never_candidate",
    "level1candidate",
    "level2candidate",
    "level3candidate",
    "l1eligible",
    "l2eligible",
    "l3eligible",
    "l3_cleared"
];

const MEMBERSHIP_JOURNEYS = [
    "never_member",
    "affiliate_member",
    "professional_member",
    "charterholder",
    "charterholderinactive",
    "professional_member_inactive",
    "affiliate_member_inactive"
];

const PROGRAM_JOURNEY_LABELS: Record<string, string> = {
    never_candidate: "Never a Candidate",
    level1candidate: "Level I Candidate",
    level2candidate: "Level II Candidate",
    level3candidate: "Level III Candidate",
    l1eligible: "Level I Eligible",
    l2eligible: "Level II Eligible",
    l3eligible: "Level III Eligible",
    l3_cleared: "Level III Cleared"
};

const MEMBERSHIP_JOURNEY_LABELS: Record<string, string> = {
    never_member: "Never a Member",
    affiliate_member: "Affiliate Member",
    professional_member: "Professional Member",
    charterholder: "Charterholder",
    charterholderinactive: "Charterholder (Inactive)",
    professional_member_inactive: "Professional Member (Inactive)",
    affiliate_member_inactive: "Affiliate Member (Inactive)"
};

const CollapsibleGroupList = ({
    title,
    groups,
    badgeStyleFn
}: {
    title: string;
    groups: GigTypeGroup[];
    badgeStyleFn: (type: string) => string;
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    if (groups.length === 0) return null;

    const showAll = isExpanded || groups.length <= 4;
    const visibleGroups = showAll ? groups : groups.slice(0, 3);
    const remainingCount = groups.length - visibleGroups.length;

    return (
        <div className="space-y-1 bg-gray-50/50 p-1.5 rounded-lg border border-gray-100/80">
            <div className="text-[9px] font-black uppercase tracking-wider text-gray-400 flex items-center justify-between">
                <span>{title} ({groups.length})</span>
            </div>
            <div className="flex flex-wrap gap-1">
                {visibleGroups.map((og, i) => (
                    <span
                        key={i}
                        className={`inline-flex items-center px-1.5 py-0.5 rounded text-[8px] font-bold border ${badgeStyleFn(og.entity_type)}`}
                    >
                        {og.entity_name || `${og.entity_type} #${og.entity_id}`}
                    </span>
                ))}
                {remainingCount > 0 && (
                    <button
                        onClick={() => setIsExpanded(true)}
                        className="inline-flex items-center px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-wider bg-gray-100 text-[#007a8a] hover:bg-gray-200 border border-gray-200 transition-colors cursor-pointer"
                    >
                        + {remainingCount} more
                    </button>
                )}
                {isExpanded && groups.length > 4 && (
                    <button
                        onClick={() => setIsExpanded(false)}
                        className="inline-flex items-center px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-wider bg-gray-100 text-red-600 hover:bg-gray-200 border border-gray-200 transition-colors cursor-pointer"
                    >
                        Show less
                    </button>
                )}
            </div>
        </div>
    );
};

export default function ConsoleGigTypesPanel() {
    const { user } = useWorkspace();
    const [gigTypes, setGigTypes] = useState<GigType[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Refs for click outside detection
    const cadenceRef = useRef<HTMLDivElement>(null);
    const discoveryRef = useRef<HTMLDivElement>(null);
    const recognitionRef = useRef<HTMLDivElement>(null);
    const groupRef = useRef<HTMLDivElement>(null);
    const statusRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (cadenceRef.current && !cadenceRef.current.contains(target)) {
                setIsCadenceDropdownOpen(false);
            }
            if (discoveryRef.current && !discoveryRef.current.contains(target)) {
                setIsDiscoveryDropdownOpen(false);
            }
            if (recognitionRef.current && !recognitionRef.current.contains(target)) {
                setIsRecognitionDropdownOpen(false);
            }
            if (groupRef.current && !groupRef.current.contains(target)) {
                setIsGroupDropdownOpen(false);
            }
            if (statusRef.current && !statusRef.current.contains(target)) {
                setIsStatusDropdownOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Form modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [cadence, setCadence] = useState("one_off");
    const [timeCommitment, setTimeCommitment] = useState("");
    const [discoveryMode, setDiscoveryMode] = useState("application");
    const [recognitionTier, setRecognitionTier] = useState("tier2");
    const [isConfidential, setIsConfidential] = useState(false);
    const [skipReview, setSkipReview] = useState(false);
    const [openToAll, setOpenToAll] = useState(true);
    const [programJourneyScopes, setProgramJourneyScopes] = useState<string[]>([]);
    const [membershipJourneyScopes, setMembershipJourneyScopes] = useState<string[]>([]);
    const [status, setStatus] = useState("canonical");

    // Dropdown open states
    const [isCadenceDropdownOpen, setIsCadenceDropdownOpen] = useState(false);
    const [isDiscoveryDropdownOpen, setIsDiscoveryDropdownOpen] = useState(false);
    const [isRecognitionDropdownOpen, setIsRecognitionDropdownOpen] = useState(false);
    const [isGroupDropdownOpen, setIsGroupDropdownOpen] = useState(false);
    const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
    const [groupSearchQuery, setGroupSearchQuery] = useState("");

    // Group selection states
    const [availableGroups, setAvailableGroups] = useState<GroupOption[]>([]);
    const [selectedGroups, setSelectedGroups] = useState<GroupOption[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);
    const [formSuccess, setFormSuccess] = useState<string | null>(null);

    const [editingGigName, setEditingGigName] = useState<GigType | null>(null);
    const [statusConfirm, setStatusConfirm] = useState<{ gig: GigType; newStatus: string } | null>(null);
    const [isConfirmingStatus, setIsConfirmingStatus] = useState(false);

    const isStaff = user ? (user.is_staff || ["admin", "superadmin", "staff"].includes((user.role || "").toLowerCase())) : false;

    const handleCreateClick = () => {
        setEditingGigName(null);
        setName("");
        setDescription("");
        setCadence("one_off");
        setTimeCommitment("");
        setDiscoveryMode("application");
        setRecognitionTier("tier2");
        setStatus("canonical");
        setIsConfidential(false);
        setSkipReview(false);
        setOpenToAll(true);
        setProgramJourneyScopes([]);
        setMembershipJourneyScopes([]);
        setSelectedGroups([]);
        setFormError(null);
        setFormSuccess(null);
        setIsModalOpen(true);
        setIsCadenceDropdownOpen(false);
        setIsDiscoveryDropdownOpen(false);
        setIsRecognitionDropdownOpen(false);
        setIsGroupDropdownOpen(false);
        setGroupSearchQuery("");
    };

    const handleEditClick = (t: GigType) => {
        setEditingGigName(t);
        setName(t.name);
        setDescription(t.description || "");
        setCadence(t.cadence);
        setTimeCommitment(t.time_commitment || "");
        setDiscoveryMode(t.default_discovery_mode);
        setRecognitionTier(t.recognition_tier);
        setStatus(t.status || "canonical");
        setIsConfidential(t.is_confidential_intake);
        setSkipReview(t.skip_staff_review);
        setOpenToAll(t.open_to_all ?? true);
        setProgramJourneyScopes(t.program_journey_scope ? t.program_journey_scope.split(",").map((s: string) => s.trim()).filter(Boolean) : []);
        setMembershipJourneyScopes(t.membership_journey_scope ? t.membership_journey_scope.split(",").map((s: string) => s.trim()).filter(Boolean) : []);
        
        if (t.owning_groups) {
            const matched: GroupOption[] = [];
            t.owning_groups.forEach(og => {
                const found = availableGroups.find(g => g.entity_type === og.entity_type && g.entity_id === og.entity_id);
                if (found) {
                    matched.push(found);
                } else {
                    matched.push({
                        entity_type: og.entity_type,
                        entity_id: og.entity_id,
                        name: `${og.entity_type.charAt(0).toUpperCase() + og.entity_type.slice(1)} #${og.entity_id}`
                    });
                }
            });
            setSelectedGroups(matched);
        } else {
            setSelectedGroups([]);
        }
        setFormError(null);
        setFormSuccess(null);
        setIsModalOpen(true);
        setIsCadenceDropdownOpen(false);
        setIsDiscoveryDropdownOpen(false);
        setIsRecognitionDropdownOpen(false);
        setIsGroupDropdownOpen(false);
        setGroupSearchQuery("");
    };

    const handleQuickStatusChange = (gig: GigType, newStatus: string) => {
        setStatusConfirm({ gig, newStatus });
    };

    const confirmStatusChange = async () => {
        if (!statusConfirm) return;
        setIsConfirmingStatus(true);
        const { gig, newStatus } = statusConfirm;
        const actionStr = newStatus === "retired" ? "retire" : "restore";
        
        setError(null);
        
        const payload = {
            name: gig.name,
            description: gig.description,
            cadence: gig.cadence,
            time_commitment: gig.time_commitment,
            default_discovery_mode: gig.default_discovery_mode,
            recognition_tier: gig.recognition_tier,
            is_confidential_intake: gig.is_confidential_intake,
            skip_staff_review: gig.skip_staff_review,
            open_to_all: gig.open_to_all ?? true,
            program_journey_scope: gig.program_journey_scope,
            membership_journey_scope: gig.membership_journey_scope,
            owning_groups: gig.owning_groups ? gig.owning_groups.map(g => ({
                entity_type: g.entity_type,
                entity_id: g.entity_id
            })) : [],
            status: newStatus
        };

        try {
            const res = await fetchWithAuth(`${API_BASE}/api/v1/gigs/types/${gig.id}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload)
            });
            if (!res.ok) {
                const errBody = await res.json().catch(() => ({}));
                setError(errBody.detail || `Failed to ${actionStr} gig category.`);
                setIsConfirmingStatus(false);
                setStatusConfirm(null);
                return;
            }
            fetchGigTypes();
            setStatusConfirm(null);
        } catch (err: any) {
            console.error(err);
            setError(`An unexpected error occurred while updating the status.`);
        } finally {
            setIsConfirmingStatus(false);
        }
    };

    const fetchGigTypes = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const url = isStaff
                ? `${API_BASE}/api/v1/gigs/types?include_all=true`
                : `${API_BASE}/api/v1/gigs/types`;
            const res = await fetchWithAuth(url);
            if (!res.ok) throw new Error("Failed to load gig names.");
            const data = await res.json() as GigType[];
            setGigTypes(data);
        } catch (err) {
            console.error(err);
            setError(err instanceof Error ? err.message : "An unexpected error occurred.");
        } finally {
            setIsLoading(false);
        }
    };

    const fetchGroups = async () => {
        try {
            const [chaptersRes, committeesRes, communitiesRes] = await Promise.all([
                fetchWithAuth(`${API_BASE}/api/v1/organizations/chapters`),
                fetchWithAuth(`${API_BASE}/api/v1/organizations/committees`),
                fetchWithAuth(`${API_BASE}/api/v1/organizations/communities`)
            ]);

            const chapters = chaptersRes.ok ? await chaptersRes.json() as GroupItem[] : [];
            const committees = committeesRes.ok ? await committeesRes.json() as GroupItem[] : [];
            const communities = communitiesRes.ok ? await communitiesRes.json() as GroupItem[] : [];

            const options: GroupOption[] = [];
            options.push({ entity_type: "staff", entity_id: 0, name: "Staff" });
            committees.forEach((c: GroupItem) => {
                options.push({ entity_type: "committee", entity_id: c.id, name: `Committee: ${c.name}` });
            });
            chapters.forEach((ch: GroupItem) => {
                options.push({ entity_type: "chapter", entity_id: ch.id, name: `Chapter: ${ch.name}` });
            });
            communities.forEach((co: GroupItem) => {
                options.push({ entity_type: "community", entity_id: co.id, name: `Community: ${co.name}` });
            });

            setAvailableGroups(options);
        } catch (err) {
            console.error("Error loading groups:", err);
        }
    };

    useEffect(() => {
        Promise.resolve().then(() => {
            fetchGigTypes();
            if (isStaff) {
                fetchGroups();
            }
        });
    }, [isStaff]);

    const handleAddGroup = (key: string) => {
        if (!key) return;

        if (key.startsWith("bulk:")) {
            const targetType = key.split(":")[1]; // "committees", "chapters", "communities"
            const singularType = targetType === "committees" ? "committee" : targetType === "chapters" ? "chapter" : "community";
            
            // Find all available groups of this type
            const groupsToAdd = availableGroups.filter(g => g.entity_type === singularType);
            
            // Filter out groups already selected
            const newGroups = groupsToAdd.filter(g => 
                !selectedGroups.some(sg => sg.entity_type === g.entity_type && sg.entity_id === g.entity_id)
            );
            
            setSelectedGroups([...selectedGroups, ...newGroups]);
            return;
        }

        const [type, idStr] = key.split(":");
        const id = parseInt(idStr, 10);

        const group = availableGroups.find(g => g.entity_type === type && g.entity_id === id);
        if (group && !selectedGroups.some(g => g.entity_type === type && g.entity_id === id)) {
            setSelectedGroups([...selectedGroups, group]);
        }
    };

    const handleRemoveGroup = (group: GroupOption) => {
        setSelectedGroups(selectedGroups.filter(g => !(g.entity_type === group.entity_type && g.entity_id === group.entity_id)));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim()) {
            setFormError("Gig Name is required.");
            return;
        }
        if (selectedGroups.length === 0) {
            setFormError("You must assign at least one owning group for this gig name.");
            return;
        }

        setIsSubmitting(true);
        setFormError(null);
        setFormSuccess(null);

        const payload = {
            name: name.trim(),
            description: description.trim() || null,
            cadence: cadence,
            time_commitment: timeCommitment.trim() || null,
            default_discovery_mode: discoveryMode,
            recognition_tier: recognitionTier,
            is_confidential_intake: isConfidential,
            skip_staff_review: skipReview,
            open_to_all: openToAll,
            program_journey_scope: !openToAll && programJourneyScopes.length > 0 ? programJourneyScopes.join(", ") : null,
            membership_journey_scope: !openToAll && membershipJourneyScopes.length > 0 ? membershipJourneyScopes.join(", ") : null,
            owning_groups: selectedGroups.map(g => ({
                entity_type: g.entity_type,
                entity_id: g.entity_id
            })),
            status: status
        };

        try {
            const url = editingGigName 
                ? `${API_BASE}/api/v1/gigs/types/${editingGigName.id}`
                : `${API_BASE}/api/v1/gigs/types`;
            const method = editingGigName ? "PUT" : "POST";

            const res = await fetchWithAuth(url, {
                method: method,
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload)
            });

            if (!res.ok) {
                const errBody = await res.json().catch(() => ({}));
                setFormError(errBody.detail || `Failed to ${editingGigName ? "update" : "create"} gig name.`);
                setIsSubmitting(false);
                return;
            }

            setFormSuccess(`Gig Name successfully ${editingGigName ? "updated" : "created"}!`);
            fetchGigTypes();

            // Reset form
            setTimeout(() => {
                setIsModalOpen(false);
                setName("");
                setDescription("");
                setCadence("one_off");
                setTimeCommitment("");
                setDiscoveryMode("application");
                setRecognitionTier("tier2");
                setIsConfidential(false);
                setSkipReview(false);
                setOpenToAll(true);
                setProgramJourneyScopes([]);
                setMembershipJourneyScopes([]);
                setSelectedGroups([]);
                setFormSuccess(null);
                setEditingGigName(null);
                setIsCadenceDropdownOpen(false);
                setIsDiscoveryDropdownOpen(false);
                setIsRecognitionDropdownOpen(false);
                setIsGroupDropdownOpen(false);
                setGroupSearchQuery("");
            }, 1500);

        } catch (err) {
            console.error(err);
            setFormError("An unexpected error occurred while saving the gig name.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const getEntityBadgeStyle = (type: string) => {
        const t = type.toLowerCase();
        if (t === "staff") return "bg-rose-50 text-rose-700 border-rose-100";
        if (t === "committee") return "bg-blue-50 text-blue-700 border-blue-100";
        if (t === "chapter") return "bg-emerald-50 text-emerald-700 border-emerald-100";
        return "bg-purple-50 text-purple-700 border-purple-100";
    };

    const cadenceOptions = [
        { value: "one_off", label: "One-Off Task" },
        { value: "ongoing", label: "Ongoing Commitment" },
        { value: "time_bound", label: "Time Bound" },
        { value: "standby", label: "Standby Availability" }
    ];

    const discoveryOptions = [
        { value: "application", label: "Application-Driven" },
        { value: "invitation", label: "Invitation-Driven" },
        { value: "standing_availability", label: "Standing Pool Draw" }
    ];

    const recognitionOptions = [
        { value: "tier2", label: "Tier 2 (Standard Recognition)" },
        { value: "tier1", label: "Tier 1 (High Recognition)" }
    ];

    const statusOptions = [
        { value: "canonical", label: "Active" },
        { value: "retired", label: "Retired" }
    ];

    return (
        <div className="space-y-6">
            {/* Header / Actions */}
            <div className="flex justify-between items-center">
                <div>
                    <h3 className="text-sm font-black text-[#003d45] uppercase tracking-wider">Gig Management</h3>
                    <p className="text-[11px] text-gray-400 mt-0.5">Manage active volunteer gig names and their group access permissions.</p>
                </div>
                {isStaff && (
                    <button
                        onClick={handleCreateClick}
                        className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#007a8a] hover:bg-[#006977] text-white rounded-xl text-xs font-bold uppercase tracking-wider transition-colors shadow-sm"
                    >
                        <Plus className="w-4 h-4" />
                        Create Gig
                    </button>
                )}
            </div>

            {error && (
                <div className="bg-red-50 text-red-700 p-4 border border-red-100 rounded-2xl text-xs flex items-center gap-2">
                    <Info className="w-4 h-4 text-red-500 shrink-0" />
                    <span><strong>Error:</strong> {error}</span>
                </div>
            )}

            {isLoading ? (
                <div className="py-12 flex flex-col items-center justify-center gap-3">
                    <div className="w-6 h-6 border-2 border-[#007a8a]/20 border-t-[#007a8a] rounded-full animate-spin" />
                    <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Loading gig names...</p>
                </div>
            ) : gigTypes.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-xl border border-gray-150 shadow-sm">
                    <BookOpen className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                    <h3 className="text-sm font-bold text-[#003d45] uppercase tracking-wider">No Gig Names Found</h3>
                    <p className="text-xs text-gray-400 mt-1">No gig names have been defined yet.</p>
                </div>
            ) : (
                <div className="bg-white rounded-xl border border-gray-150 shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse font-sans">
                            <thead>
                                {/* Top Header Row */}
                                <tr className="bg-[#a9c4bb]/30 text-[10.5px] font-bold uppercase tracking-wider text-[#003d45] border-b border-gray-250">
                                    <th className="py-3.5 px-6">Gig Details</th>
                                    <th className="py-3.5 px-6">Cadence / Discovery</th>
                                    <th className="py-3.5 px-6">Owning Groups</th>
                                    <th className="py-3.5 px-6">Attributes</th>
                                    {isStaff && <th className="py-3.5 px-6 text-right">Actions</th>}
                                </tr>
                                {/* Sub Header Row (Descriptions) */}
                                <tr className="border-b border-gray-150 text-[10px] text-gray-400 font-medium bg-white">
                                    <td className="py-2 px-6">The gig title & description</td>
                                    <td className="py-2 px-6">Time commitment & options</td>
                                    <td className="py-2 px-6">The group who can post the gig and is responsible for it</td>
                                    <td className="py-2 px-6">Review policies & Eligibility</td>
                                    {isStaff && <td className="py-2 px-6 text-right">Settings</td>}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 text-xs text-gray-700 bg-white">
                                {gigTypes.map((t) => (
                                    <tr key={t.id} className="hover:bg-gray-50/50 transition-colors">
                                        {/* Details */}
                                        <td className="py-4.5 px-6 max-w-xs">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-bold text-[#003d45] text-[13.5px] leading-tight">{t.name}</span>
                                                {t.status && t.status !== "canonical" && (
                                                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[8.5px] font-black uppercase tracking-wider ${t.status === "retired" ? "bg-red-50 text-red-700 border border-red-100" : "bg-amber-50 text-amber-800 border border-amber-100"}`}>
                                                        {t.status}
                                                    </span>
                                                )}
                                            </div>
                                            {t.description && (
                                                <div className="text-[10px] text-gray-400 mt-1 line-clamp-2 leading-relaxed font-medium">
                                                    {t.description}
                                                </div>
                                            )}
                                        </td>

                                        {/* Cadence & Discovery */}
                                        <td className="py-4.5 px-6 space-y-1">
                                            <div className="flex items-center gap-1.5 text-[10px] text-gray-500 font-medium">
                                                <Clock className="w-3.5 h-3.5 text-gray-400" />
                                                <span>Cadence: <strong className="text-gray-600 font-bold">{t.cadence.replace(/_/g, " ")}</strong></span>
                                            </div>
                                            <div className="flex items-center gap-1.5 text-[10px] text-gray-500 font-medium">
                                                <Users className="w-3.5 h-3.5 text-gray-400" />
                                                <span>Discovery: <strong className="text-gray-600 font-bold">{t.default_discovery_mode.replace(/_/g, " ")}</strong></span>
                                            </div>
                                        </td>

                                        {/* Owning Groups */}
                                        <td className="py-4.5 px-6">
                                            {!t.owning_groups || t.owning_groups.length === 0 ? (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wider bg-gray-100 text-gray-600 border border-gray-250 font-medium">
                                                    Global (All Groups)
                                                </span>
                                            ) : (
                                                <div className="space-y-2 max-w-sm">
                                                    {(() => {
                                                        const committees = t.owning_groups.filter(g => g.entity_type.toLowerCase() === "committee");
                                                        const chapters = t.owning_groups.filter(g => g.entity_type.toLowerCase() === "chapter");
                                                        const communities = t.owning_groups.filter(g => g.entity_type.toLowerCase() === "community");
                                                        const staff = t.owning_groups.filter(g => g.entity_type.toLowerCase() === "staff");
                                                        return (
                                                            <>
                                                                <CollapsibleGroupList title="Staff" groups={staff} badgeStyleFn={getEntityBadgeStyle} />
                                                                <CollapsibleGroupList title="Committees" groups={committees} badgeStyleFn={getEntityBadgeStyle} />
                                                                <CollapsibleGroupList title="Chapters" groups={chapters} badgeStyleFn={getEntityBadgeStyle} />
                                                                <CollapsibleGroupList title="Communities" groups={communities} badgeStyleFn={getEntityBadgeStyle} />
                                                            </>
                                                        );
                                                    })()}
                                                </div>
                                            )}
                                        </td>

                                        {/* Attributes */}
                                        <td className="py-4.5 px-6 space-y-1.5">
                                            {t.open_to_all ? (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[9px] font-bold uppercase bg-emerald-55 text-emerald-700 border border-emerald-100 font-medium block w-max">
                                                    Open to All
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[9px] font-bold uppercase bg-amber-50 text-amber-700 border border-amber-100 font-medium block w-max">
                                                    Restricted
                                                </span>
                                            )}
                                            {t.is_confidential_intake && (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[9px] font-bold uppercase bg-red-50 text-red-700 border border-red-100 font-medium block w-max">
                                                    Confidential
                                                </span>
                                            )}
                                            {t.skip_staff_review && (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[9px] font-bold uppercase bg-[#cbeef2]/30 text-[#007a8a] border border-[#007a8a]/20 font-medium block w-max">
                                                    Auto-Approve
                                                </span>
                                            )}
                                        </td>

                                        {/* Actions */}
                                        {isStaff && (
                                            <td className="py-4.5 px-6 text-right whitespace-nowrap space-x-2">
                                                <button
                                                    onClick={() => handleEditClick(t)}
                                                    className="text-xs font-bold text-[#007a8a] hover:text-[#006977] transition-colors cursor-pointer border-none bg-transparent"
                                                >
                                                    Edit
                                                </button>
                                                <span className="text-gray-250">|</span>
                                                {t.status === "retired" ? (
                                                    <button
                                                        onClick={() => handleQuickStatusChange(t, "canonical")}
                                                        className="text-xs font-bold text-emerald-600 hover:text-emerald-800 transition-colors cursor-pointer border-none bg-transparent"
                                                    >
                                                        Restore
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={() => handleQuickStatusChange(t, "retired")}
                                                        className="text-xs font-bold text-rose-600 hover:text-rose-800 transition-colors cursor-pointer border-none bg-transparent"
                                                    >
                                                        Retire
                                                    </button>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Create Category Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-[#003d45]/45 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white border border-gray-150 shadow-2xl rounded-xl w-full max-w-2xl overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
                        {/* Header */}
                        <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
                            <div>
                                <h3 className="text-sm font-black text-[#003d45] uppercase tracking-wider flex items-center gap-2">
                                    <Shield className="w-4 h-4 text-[#007a8a]" />
                                    {editingGigName ? "Edit Gig" : "Create Gig"}
                                </h3>
                                <p className="text-[10px] text-gray-400 mt-1">
                                    {editingGigName ? "Modify the configuration and group ownership for this gig name." : "Define a new volunteer role template and assign group ownership."}
                                </p>
                            </div>
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        {/* Form */}
                        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
                            {formError && (
                                <div className="bg-red-50 text-red-700 p-4 border border-red-100 rounded-2xl text-xs flex items-center gap-2">
                                    <ShieldAlert className="w-4 h-4 text-red-500 shrink-0" />
                                    <span><strong>Error:</strong> {formError}</span>
                                </div>
                            )}
                            {formSuccess && (
                                <div className="bg-emerald-50 text-emerald-700 p-4 border border-emerald-100 rounded-2xl text-xs flex items-center gap-2 font-bold animate-pulse">
                                    <Check className="w-4 h-4 text-emerald-500 shrink-0" />
                                    <span>{formSuccess}</span>
                                </div>
                            )}

                            {/* Basic Details */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <label className="text-xs font-bold text-[#004d57] uppercase tracking-wider block mb-1.5">Gig Name</label>
                                    <input
                                        type="text"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        placeholder="e.g. Chapter Content Reviewer"
                                        className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-xs focus:outline-none focus:border-[#007a8a] focus:ring-1 focus:ring-[#007a8a] font-semibold text-gray-700 hover:border-gray-300 transition-colors shadow-sm"
                                        disabled={isSubmitting || !!formSuccess}
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-xs font-bold text-[#004d57] uppercase tracking-wider block mb-1.5">Cadence</label>
                                    <div className="relative" ref={cadenceRef}>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setIsCadenceDropdownOpen(!isCadenceDropdownOpen);
                                                setIsDiscoveryDropdownOpen(false);
                                                setIsRecognitionDropdownOpen(false);
                                                setIsGroupDropdownOpen(false);
                                            }}
                                            className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-xs bg-white text-gray-700 focus:outline-none focus:border-[#007a8a] focus:ring-1 focus:ring-[#007a8a] font-semibold flex items-center justify-between shadow-sm transition-all cursor-pointer hover:border-gray-300"
                                            disabled={isSubmitting || !!formSuccess}
                                        >
                                            <span className="truncate">
                                                {cadenceOptions.find(o => o.value === cadence)?.label || "-- Select Cadence --"}
                                            </span>
                                            <ChevronDown className="h-4 w-4 text-gray-400 shrink-0 ml-2" />
                                        </button>
                                        {isCadenceDropdownOpen && (
                                            <div className="absolute left-0 right-0 mt-1.5 bg-white border border-gray-150 rounded-xl shadow-xl z-20 max-h-60 overflow-y-auto py-1 font-sans text-xs flex flex-col divide-y divide-gray-50">
                                                {cadenceOptions.map(o => (
                                                    <button
                                                        key={o.value}
                                                        type="button"
                                                        onClick={() => {
                                                            setCadence(o.value);
                                                            setIsCadenceDropdownOpen(false);
                                                        }}
                                                        className={`w-full text-left px-4 py-2 hover:bg-[#c3d6d9]/30 transition-colors font-semibold ${cadence === o.value ? "bg-[#007a8a]/10 text-[#004d57] font-bold" : "text-gray-700"}`}
                                                    >
                                                        {o.label}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-1.5">
                                <label className="text-xs font-bold text-[#004d57] uppercase tracking-wider block mb-1.5">Description</label>
                                <textarea
                                    rows={3}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="Describe the primary responsibilities and expectations for this role..."
                                    className="w-full border border-gray-200 rounded-xl p-3 text-xs focus:outline-none focus:border-[#007a8a] focus:ring-1 focus:ring-[#007a8a] font-medium text-gray-700 leading-relaxed hover:border-gray-300 transition-colors shadow-sm"
                                    disabled={isSubmitting || !!formSuccess}
                                />
                            </div>

                             <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="space-y-1.5">
                                    <label className="text-xs font-bold text-[#004d57] uppercase tracking-wider block mb-1.5">Discovery Mode</label>
                                    <div className="relative" ref={discoveryRef}>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setIsDiscoveryDropdownOpen(!isDiscoveryDropdownOpen);
                                                setIsCadenceDropdownOpen(false);
                                                setIsRecognitionDropdownOpen(false);
                                                setIsGroupDropdownOpen(false);
                                                setIsStatusDropdownOpen(false);
                                            }}
                                            className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-xs bg-white text-gray-700 focus:outline-none focus:border-[#007a8a] focus:ring-1 focus:ring-[#007a8a] font-semibold flex items-center justify-between shadow-sm transition-all cursor-pointer hover:border-gray-300"
                                            disabled={isSubmitting || !!formSuccess}
                                        >
                                            <span className="truncate">
                                                {discoveryOptions.find(o => o.value === discoveryMode)?.label || "-- Select Discovery Mode --"}
                                            </span>
                                            <ChevronDown className="h-4 w-4 text-gray-400 shrink-0 ml-2" />
                                        </button>
                                        {isDiscoveryDropdownOpen && (
                                            <div className="absolute left-0 right-0 mt-1.5 bg-white border border-gray-150 rounded-xl shadow-xl z-20 max-h-60 overflow-y-auto py-1 font-sans text-xs flex flex-col divide-y divide-gray-50">
                                                {discoveryOptions.map(o => (
                                                    <button
                                                        key={o.value}
                                                        type="button"
                                                        onClick={() => {
                                                            setDiscoveryMode(o.value);
                                                            setIsDiscoveryDropdownOpen(false);
                                                        }}
                                                        className={`w-full text-left px-4 py-2 hover:bg-[#c3d6d9]/30 transition-colors font-semibold ${discoveryMode === o.value ? "bg-[#007a8a]/10 text-[#004d57] font-bold" : "text-gray-700"}`}
                                                    >
                                                        {o.label}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-xs font-bold text-[#004d57] uppercase tracking-wider block mb-1.5">Recognition Tier</label>
                                    <div className="relative" ref={recognitionRef}>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setIsRecognitionDropdownOpen(!isRecognitionDropdownOpen);
                                                setIsCadenceDropdownOpen(false);
                                                setIsDiscoveryDropdownOpen(false);
                                                setIsGroupDropdownOpen(false);
                                                setIsStatusDropdownOpen(false);
                                            }}
                                            className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-xs bg-white text-gray-700 focus:outline-none focus:border-[#007a8a] focus:ring-1 focus:ring-[#007a8a] font-semibold flex items-center justify-between shadow-sm transition-all cursor-pointer hover:border-gray-300"
                                            disabled={isSubmitting || !!formSuccess}
                                        >
                                            <span className="truncate">
                                                {recognitionOptions.find(o => o.value === recognitionTier)?.label || "-- Select Recognition Tier --"}
                                            </span>
                                            <ChevronDown className="h-4 w-4 text-gray-400 shrink-0 ml-2" />
                                        </button>
                                        {isRecognitionDropdownOpen && (
                                            <div className="absolute left-0 right-0 mt-1.5 bg-white border border-gray-150 rounded-xl shadow-xl z-20 max-h-60 overflow-y-auto py-1 font-sans text-xs flex flex-col divide-y divide-gray-50">
                                                {recognitionOptions.map(o => (
                                                    <button
                                                        key={o.value}
                                                        type="button"
                                                        onClick={() => {
                                                            setRecognitionTier(o.value);
                                                            setIsRecognitionDropdownOpen(false);
                                                        }}
                                                        className={`w-full text-left px-4 py-2 hover:bg-[#c3d6d9]/30 transition-colors font-semibold ${recognitionTier === o.value ? "bg-[#007a8a]/10 text-[#004d57] font-bold" : "text-gray-755"}`}
                                                    >
                                                        {o.label}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-xs font-bold text-[#004d57] uppercase tracking-wider block mb-1.5">Status</label>
                                    <div className="relative" ref={statusRef}>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setIsStatusDropdownOpen(!isStatusDropdownOpen);
                                                setIsCadenceDropdownOpen(false);
                                                setIsDiscoveryDropdownOpen(false);
                                                setIsRecognitionDropdownOpen(false);
                                                setIsGroupDropdownOpen(false);
                                            }}
                                            className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-xs bg-white text-gray-700 focus:outline-none focus:border-[#007a8a] focus:ring-1 focus:ring-[#007a8a] font-semibold flex items-center justify-between shadow-sm transition-all cursor-pointer hover:border-gray-300"
                                            disabled={isSubmitting || !!formSuccess}
                                        >
                                            <span className="truncate">
                                                {statusOptions.find(o => o.value === status)?.label || "-- Select Status --"}
                                            </span>
                                            <ChevronDown className="h-4 w-4 text-gray-400 shrink-0 ml-2" />
                                        </button>
                                        {isStatusDropdownOpen && (
                                            <div className="absolute left-0 right-0 mt-1.5 bg-white border border-gray-150 rounded-xl shadow-xl z-20 max-h-60 overflow-y-auto py-1 font-sans text-xs flex flex-col divide-y divide-gray-50">
                                                {statusOptions.map(o => (
                                                    <button
                                                        key={o.value}
                                                        type="button"
                                                        onClick={() => {
                                                            setStatus(o.value);
                                                            setIsStatusDropdownOpen(false);
                                                        }}
                                                        className={`w-full text-left px-4 py-2 hover:bg-[#c3d6d9]/30 transition-colors font-semibold ${status === o.value ? "bg-[#007a8a]/10 text-[#004d57] font-bold" : "text-gray-700"}`}
                                                    >
                                                        {o.label}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Checkboxes */}
                            <div className="flex gap-6 pt-2">
                                <label className="flex items-center gap-2 cursor-pointer select-none">
                                    <input
                                        type="checkbox"
                                        checked={isConfidential}
                                        onChange={(e) => setIsConfidential(e.target.checked)}
                                        className="rounded border-gray-300 text-[#007a8a] focus:ring-[#007a8a] w-4 h-4"
                                        disabled={isSubmitting || !!formSuccess}
                                    />
                                    <span className="text-[11px] font-bold text-gray-600 uppercase tracking-wide">Confidential Intake</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer select-none">
                                    <input
                                        type="checkbox"
                                        checked={skipReview}
                                        onChange={(e) => setSkipReview(e.target.checked)}
                                        className="rounded border-gray-300 text-[#007a8a] focus:ring-[#007a8a] w-4 h-4"
                                        disabled={isSubmitting || !!formSuccess}
                                    />
                                    <span className="text-[11px] font-bold text-gray-600 uppercase tracking-wide">Skip Staff Review</span>
                                </label>
                            </div>

                            {/* Targeting / Eligibility Section */}
                            <div className="border-t border-gray-100 pt-4 space-y-4">
                                <div>
                                    <label className="text-xs font-bold text-[#004d57] uppercase tracking-wider block mb-0.5">Targeting & Eligibility</label>
                                    <span className="text-[10px] text-gray-400 font-medium block">Specify default volunteer eligibility requirements. Volunteers can edit these settings when creating a posting.</span>
                                </div>

                                <div 
                                    onClick={() => {
                                        if (!isSubmitting && !formSuccess) {
                                            setOpenToAll(!openToAll);
                                            if (!openToAll) {
                                                setProgramJourneyScopes([]);
                                                setMembershipJourneyScopes([]);
                                            }
                                        }
                                    }}
                                    className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer select-none transition-all ${
                                        openToAll 
                                            ? "bg-[#c3d6d9]/45 border-[#004d57]/30 text-[#004d57] ring-1 ring-[#004d57]/10" 
                                            : "bg-gray-50/50 border-gray-200 text-gray-700 hover:bg-gray-50"
                                    }`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={openToAll}
                                        onChange={() => {}} // toggled via parent click
                                        className="w-4 h-4 rounded border-gray-350 text-[#004d57] focus:ring-[#007a8a] shrink-0 pointer-events-none"
                                        disabled={isSubmitting || !!formSuccess}
                                    />
                                    <div className="text-xs font-semibold">
                                        <span className="font-sans font-bold text-[#004d57] mr-1.5 font-bold uppercase tracking-wider text-[10.5px]">Open to all</span>
                                        <span className="text-[10.5px] text-gray-500 font-medium">— anyone, including non-members</span>
                                    </div>
                                </div>

                                {!openToAll && (
                                    <div className="bg-gray-50/50 border border-gray-150 rounded-2xl p-4.5 space-y-4 animate-in slide-in-from-top-3 duration-300">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {/* Program Journey Scope Checkboxes */}
                                            <div className="space-y-2">
                                                <span className="text-xs font-bold text-[#004d57] uppercase tracking-wider block">
                                                    Program Journey Scope
                                                </span>
                                                <div className="space-y-1.5 max-h-48 overflow-y-auto pr-2">
                                                    {PROGRAM_JOURNEYS.map((j) => {
                                                        const isChecked = programJourneyScopes.includes(j);
                                                        const label = PROGRAM_JOURNEY_LABELS[j] || j;
                                                        return (
                                                            <label key={j} className="flex items-center gap-2 cursor-pointer select-none">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isChecked}
                                                                    onChange={() => {
                                                                        if (isChecked) {
                                                                            setProgramJourneyScopes(programJourneyScopes.filter(s => s !== j));
                                                                        } else {
                                                                            setProgramJourneyScopes([...programJourneyScopes, j]);
                                                                        }
                                                                    }}
                                                                    className="rounded border-gray-300 text-[#007a8a] focus:ring-[#007a8a] w-3.5 h-3.5"
                                                                    disabled={isSubmitting || !!formSuccess}
                                                                />
                                                                <span className="text-[11px] text-gray-600 font-semibold">{label}</span>
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            </div>

                                            {/* Membership Journey Scope Checkboxes */}
                                            <div className="space-y-2">
                                                <span className="text-xs font-bold text-[#004d57] uppercase tracking-wider block">
                                                    Membership Journey Scope
                                                </span>
                                                <div className="space-y-1.5 max-h-48 overflow-y-auto pr-2">
                                                    {MEMBERSHIP_JOURNEYS.map((j) => {
                                                        const isChecked = membershipJourneyScopes.includes(j);
                                                        const label = MEMBERSHIP_JOURNEY_LABELS[j] || j;
                                                        return (
                                                            <label key={j} className="flex items-center gap-2 cursor-pointer select-none">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isChecked}
                                                                    onChange={() => {
                                                                        if (isChecked) {
                                                                            setMembershipJourneyScopes(membershipJourneyScopes.filter(s => s !== j));
                                                                        } else {
                                                                            setMembershipJourneyScopes([...membershipJourneyScopes, j]);
                                                                        }
                                                                    }}
                                                                    className="rounded border-gray-300 text-[#007a8a] focus:ring-[#007a8a] w-3.5 h-3.5"
                                                                    disabled={isSubmitting || !!formSuccess}
                                                                />
                                                                <span className="text-[11px] text-gray-600 font-semibold">{label}</span>
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Group Ownership Section */}
                            <div className="border-t border-gray-100 pt-4 space-y-3">
                                <div>
                                    <label className="text-xs font-bold text-[#004d57] uppercase tracking-wider block mb-0.5">Assigned Owning Groups</label>
                                    <span className="text-[10px] text-gray-400 font-medium block">Add the committees, chapters, or communities that own and have access to this gig name.</span>
                                </div>

                                <div className="relative" ref={groupRef}>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setIsGroupDropdownOpen(!isGroupDropdownOpen);
                                            setGroupSearchQuery("");
                                            setIsCadenceDropdownOpen(false);
                                            setIsDiscoveryDropdownOpen(false);
                                            setIsRecognitionDropdownOpen(false);
                                        }}
                                        className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-xs bg-white text-gray-700 focus:outline-none focus:border-[#007a8a] focus:ring-1 focus:ring-[#007a8a] font-semibold flex items-center justify-between shadow-sm transition-all cursor-pointer hover:border-gray-300"
                                        disabled={isSubmitting || !!formSuccess}
                                    >
                                        <span className="truncate">
                                            -- Choose group to add --
                                        </span>
                                        <ChevronDown className="h-4 w-4 text-gray-400 shrink-0 ml-2" />
                                    </button>

                                    {isGroupDropdownOpen && (
                                        <div className="absolute left-0 right-0 mt-1.5 bg-white border border-gray-150 rounded-xl shadow-xl z-20 max-h-72 overflow-y-auto py-1 font-sans text-xs flex flex-col divide-y divide-gray-50">
                                            {availableGroups.length > 5 && (
                                                <div className="p-2 bg-white sticky top-0 z-10 border-b border-gray-100 shrink-0">
                                                    <input
                                                        type="text"
                                                        placeholder="Search groups..."
                                                        value={groupSearchQuery}
                                                        onChange={(e) => setGroupSearchQuery(e.target.value)}
                                                        className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:border-[#007a8a]"
                                                    />
                                                </div>
                                            )}

                                            {!groupSearchQuery && (
                                                <div className="py-1 shrink-0">
                                                    <span className="px-4 py-1 text-[9px] font-bold uppercase tracking-wider text-[#007a8a] block bg-gray-50 select-none">
                                                        Bulk Actions
                                                    </span>
                                                    <div className="flex flex-col">
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                handleAddGroup("bulk:committees");
                                                                setIsGroupDropdownOpen(false);
                                                            }}
                                                            className="w-full text-left px-4 py-2 hover:bg-[#c3d6d9]/30 transition-colors font-semibold text-gray-700"
                                                        >
                                                            Add All Committees
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                handleAddGroup("bulk:chapters");
                                                                setIsGroupDropdownOpen(false);
                                                            }}
                                                            className="w-full text-left px-4 py-2 hover:bg-[#c3d6d9]/30 transition-colors font-semibold text-gray-700"
                                                        >
                                                            Add All Chapters
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                handleAddGroup("bulk:communities");
                                                                setIsGroupDropdownOpen(false);
                                                            }}
                                                            className="w-full text-left px-4 py-2 hover:bg-[#c3d6d9]/30 transition-colors font-semibold text-gray-700"
                                                        >
                                                            Add All Communities
                                                        </button>
                                                    </div>
                                                </div>
                                            )}

                                            {availableGroups.filter(g => g.entity_type === "staff" && g.name.toLowerCase().includes(groupSearchQuery.toLowerCase())).length > 0 && (
                                                <div className="py-1">
                                                    <span className="px-4 py-1 text-[9px] font-bold uppercase tracking-wider text-[#007a8a] block bg-gray-50 select-none">
                                                        Staff
                                                    </span>
                                                    <div className="flex flex-col">
                                                        {availableGroups.filter(g => g.entity_type === "staff" && g.name.toLowerCase().includes(groupSearchQuery.toLowerCase())).map(g => {
                                                            const key = `${g.entity_type}:${g.entity_id}`;
                                                            const isSelected = selectedGroups.some(sg => sg.entity_type === g.entity_type && sg.entity_id === g.entity_id);
                                                            return (
                                                                <button
                                                                    key={key}
                                                                    type="button"
                                                                    onClick={() => {
                                                                        if (!isSelected) {
                                                                            handleAddGroup(key);
                                                                        } else {
                                                                            handleRemoveGroup(g);
                                                                        }
                                                                        setIsGroupDropdownOpen(false);
                                                                    }}
                                                                    className={`w-full text-left px-4 py-1.5 hover:bg-[#c3d6d9]/30 transition-colors font-semibold ${isSelected ? "bg-[#007a8a]/10 text-[#004d57] font-bold" : "text-gray-700"}`}
                                                                >
                                                                    {g.name}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}

                                            {availableGroups.filter(g => g.entity_type === "committee" && g.name.toLowerCase().includes(groupSearchQuery.toLowerCase())).length > 0 && (
                                                <div className="py-1">
                                                    <span className="px-4 py-1 text-[9px] font-bold uppercase tracking-wider text-[#007a8a] block bg-gray-50 select-none">
                                                        Committees
                                                    </span>
                                                    <div className="flex flex-col">
                                                        {availableGroups.filter(g => g.entity_type === "committee" && g.name.toLowerCase().includes(groupSearchQuery.toLowerCase())).map(g => {
                                                            const key = `${g.entity_type}:${g.entity_id}`;
                                                            const isSelected = selectedGroups.some(sg => sg.entity_type === g.entity_type && sg.entity_id === g.entity_id);
                                                            return (
                                                                <button
                                                                    key={key}
                                                                    type="button"
                                                                    onClick={() => {
                                                                        if (!isSelected) {
                                                                            handleAddGroup(key);
                                                                        } else {
                                                                            handleRemoveGroup(g);
                                                                        }
                                                                        setIsGroupDropdownOpen(false);
                                                                    }}
                                                                    className={`w-full text-left px-4 py-1.5 hover:bg-[#c3d6d9]/30 transition-colors font-semibold ${isSelected ? "bg-[#007a8a]/10 text-[#004d57] font-bold" : "text-gray-700"}`}
                                                                >
                                                                    {g.name.replace("Committee: ", "")}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}

                                            {availableGroups.filter(g => g.entity_type === "chapter" && g.name.toLowerCase().includes(groupSearchQuery.toLowerCase())).length > 0 && (
                                                <div className="py-1">
                                                    <span className="px-4 py-1 text-[9px] font-bold uppercase tracking-wider text-[#007a8a] block bg-gray-50 select-none">
                                                        Chapters
                                                    </span>
                                                    <div className="flex flex-col">
                                                        {availableGroups.filter(g => g.entity_type === "chapter" && g.name.toLowerCase().includes(groupSearchQuery.toLowerCase())).map(g => {
                                                            const key = `${g.entity_type}:${g.entity_id}`;
                                                            const isSelected = selectedGroups.some(sg => sg.entity_type === g.entity_type && sg.entity_id === g.entity_id);
                                                            return (
                                                                <button
                                                                    key={key}
                                                                    type="button"
                                                                    onClick={() => {
                                                                        if (!isSelected) {
                                                                            handleAddGroup(key);
                                                                        } else {
                                                                            handleRemoveGroup(g);
                                                                        }
                                                                        setIsGroupDropdownOpen(false);
                                                                    }}
                                                                    className={`w-full text-left px-4 py-1.5 hover:bg-[#c3d6d9]/30 transition-colors font-semibold ${isSelected ? "bg-[#007a8a]/10 text-[#004d57] font-bold" : "text-gray-700"}`}
                                                                >
                                                                    {g.name.replace("Chapter: ", "")}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}

                                            {availableGroups.filter(g => g.entity_type === "community" && g.name.toLowerCase().includes(groupSearchQuery.toLowerCase())).length > 0 && (
                                                <div className="py-1">
                                                    <span className="px-4 py-1 text-[9px] font-bold uppercase tracking-wider text-[#007a8a] block bg-gray-50 select-none">
                                                        Communities
                                                    </span>
                                                    <div className="flex flex-col">
                                                        {availableGroups.filter(g => g.entity_type === "community" && g.name.toLowerCase().includes(groupSearchQuery.toLowerCase())).map(g => {
                                                            const key = `${g.entity_type}:${g.entity_id}`;
                                                            const isSelected = selectedGroups.some(sg => sg.entity_type === g.entity_type && sg.entity_id === g.entity_id);
                                                            return (
                                                                <button
                                                                    key={key}
                                                                    type="button"
                                                                    onClick={() => {
                                                                        if (!isSelected) {
                                                                            handleAddGroup(key);
                                                                        } else {
                                                                            handleRemoveGroup(g);
                                                                        }
                                                                        setIsGroupDropdownOpen(false);
                                                                    }}
                                                                    className={`w-full text-left px-4 py-1.5 hover:bg-[#c3d6d9]/30 transition-colors font-semibold ${isSelected ? "bg-[#007a8a]/10 text-[#004d57] font-bold" : "text-gray-700"}`}
                                                                >
                                                                    {g.name.replace("Community: ", "")}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Selected Groups List */}
                                <div className="flex flex-wrap gap-2 pt-1 min-h-[40px] p-3 bg-gray-50 rounded-2xl border border-gray-100">
                                    {selectedGroups.length === 0 ? (
                                        <span className="text-[10px] text-gray-400 italic self-center">No groups selected. At least one group must be added.</span>
                                    ) : (
                                        selectedGroups.map((g, idx) => (
                                            <span
                                                key={idx}
                                                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-xl text-[10px] font-bold border ${getEntityBadgeStyle(g.entity_type)}`}
                                            >
                                                {g.name}
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveGroup(g)}
                                                    className="hover:text-red-600 transition-colors"
                                                    disabled={isSubmitting || !!formSuccess}
                                                >
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </span>
                                        ))
                                    )}
                                </div>
                            </div>

                            {/* Submit */}
                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 font-sans">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="px-5 py-2.5 rounded-xl border border-gray-200 text-xs font-bold uppercase tracking-wider text-gray-500 hover:bg-gray-50 transition-colors"
                                    disabled={isSubmitting}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="bg-[#003d45] text-white hover:bg-[#002b30] text-xs font-black uppercase tracking-widest px-6 py-2.5 rounded-xl transition-colors shadow-md"
                                    disabled={isSubmitting || !!formSuccess}
                                >
                                    {isSubmitting ? (editingGigName ? "Saving..." : "Creating...") : (editingGigName ? "Save Changes" : "Create Gig")}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Status Confirm Modal */}
            {statusConfirm && (
                <div className="fixed inset-0 bg-[#003d45]/45 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white border border-gray-150 shadow-2xl rounded-xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="p-6">
                            <div className="flex items-start gap-4">
                                <div className={`p-3 rounded-full ${statusConfirm.newStatus === 'retired' ? 'bg-rose-100 text-rose-600' : 'bg-emerald-100 text-emerald-600'}`}>
                                    <ShieldAlert className="w-6 h-6" />
                                </div>
                                <div>
                                    <h3 className="text-base font-bold text-[#003d45]">
                                        {statusConfirm.newStatus === 'retired' ? 'Retire Category' : 'Restore Category'}
                                    </h3>
                                    <p className="text-sm text-gray-500 mt-2">
                                        Are you sure you want to {statusConfirm.newStatus === 'retired' ? 'retire' : 'restore'} the gig category <span className="font-bold text-gray-700">"{statusConfirm.gig.name}"</span>?
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
                            <button
                                onClick={() => setStatusConfirm(null)}
                                disabled={isConfirmingStatus}
                                className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-bold text-gray-600 hover:bg-gray-100 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmStatusChange}
                                disabled={isConfirmingStatus}
                                className={`px-4 py-2 rounded-lg text-sm font-bold text-white transition-colors shadow-sm ${
                                    statusConfirm.newStatus === 'retired' 
                                        ? 'bg-rose-600 hover:bg-rose-700' 
                                        : 'bg-emerald-600 hover:bg-emerald-700'
                                }`}
                            >
                                {isConfirmingStatus ? 'Processing...' : statusConfirm.newStatus === 'retired' ? 'Yes, Retire' : 'Yes, Restore'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
